/**
 * Approved copy and structured content.
 *
 * Every string here is reproduced verbatim from the build brief, including its
 * en-dashes and em-dashes. This is regulated content for a listed issuer: it is
 * not paraphrased, shortened or "improved". Where copy was not supplied, the
 * page renders <PendingContent> instead of invented prose.
 *
 * The literal words "the Group" are deliberately brand-neutral and survive the
 * rename untouched, so they are hardcoded rather than templated.
 */

import { HERITAGE_YEARS } from './heritage';

/* ── Navigation ── exact order; Investor Relations sits second on purpose ── */
export const PRIMARY_NAV = [
  { label: 'Home', to: '/' },
  { label: 'Investor Relations', to: '/investor-relations' },
  { label: 'The Group', to: '/the-group' },
  { label: 'Our Businesses', to: '/our-businesses' },
  { label: 'Governance', to: '/governance' },
  { label: 'Sustainability', to: '/sustainability' },
  { label: 'Newsroom', to: '/newsroom' },
  { label: 'Contact', to: '/contact' },
];

export const FOOTER_COLUMNS = [
  {
    heading: 'Company',
    links: [
      { label: 'Home', to: '/' },
      { label: 'The Group', to: '/the-group' },
      { label: 'Our Businesses', to: '/our-businesses' },
      { label: 'Governance', to: '/governance' },
    ],
  },
  {
    heading: 'Our Businesses',
    links: [
      { label: 'Supratechnic', to: '/our-businesses#distribution' },
      { label: 'SII', to: '/our-businesses#sii' },
      { label: 'SuprAI Pte Ltd', to: '/our-businesses#suprai' },
      { label: 'SupraYi', to: '/our-businesses#suprayi' },
    ],
  },
  {
    heading: 'Investors',
    links: [
      { label: 'Announcements', to: '/investor-relations/announcements' },
      { label: 'Financial results', to: '/investor-relations/financial-results' },
      { label: 'Annual reports', to: '/investor-relations/annual-reports' },
      { label: 'Share information', to: '/investor-relations/share-information' },
      { label: 'Email alerts', to: '/investor-relations/email-alerts' },
      { label: 'IR contact', to: '/investor-relations/contact' },
    ],
  },
  {
    heading: 'Connect',
    links: [
      { label: 'Contact', to: '/contact' },
      { label: 'Newsroom', to: '/newsroom' },
    ],
  },
];

export const LEGAL_LINKS = [
  { label: 'Terms of Use', to: '/terms-of-use' },
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Disclaimer', to: '/disclaimer' },
  { label: 'Cookie Policy', to: '/cookie-policy' },
  { label: 'Important Notice', to: '/important-notice' },
];

/* ── Home ──────────────────────────────────────────────── */
export const HOME = {
  hero: {
    eyebrow: 'SGX Mainboard · Singapore · Malaysia · Indonesia',
    h1: 'A heritage of distribution. A future built on intelligence.',
    sub:
      'The Group is a multi-country group spanning marine, industrial, healthcare and scientific instrumentation — now building the AI, automation and MarTech capabilities that will define its next chapter.',
    primary: { label: 'Investor Relations →', to: '/investor-relations' },
    secondary: { label: 'Explore the Group', to: '/the-group' },
  },
  latest: {
    eyebrow: 'Latest from the Group',
    heading: 'Regulatory announcements',
    viewAll: { label: 'View all announcements →', to: '/investor-relations/announcements' },
    empty: 'Announcements will appear here once the Group resumes filing on SGXNet.',
  },
  stats: [
    { value: HERITAGE_YEARS, suffix: '+', label: 'Years of regional distribution' },
    { value: 3, suffix: '', label: 'Countries — Singapore, Malaysia, Indonesia' },
    { value: 10, suffix: '+', label: 'Brand lines represented' },
    { literal: 'SGX', label: 'Mainboard listed' },
  ],
  ecosystem: {
    eyebrow: 'The Ecosystem',
    h2: 'One group. Three pillars. A single flywheel.',
    intro:
      'Our businesses are built to reinforce one another — a revenue engine that generates real-world data, an intelligence layer that turns that data into tools, and a capability accelerator that compounds it all.',
    cards: [
      {
        key: 'distribution',
        label: 'Distribution & Instrumentation',
        title: 'The Revenue Engine',
        copy:
          `Supratechnic and SII — ${HERITAGE_YEARS}+ years of trusted regional distribution across marine, industrial, healthcare and scientific instrumentation. The cash-generating backbone of the Group.`,
        link: { label: 'Explore the businesses →', to: '/our-businesses#distribution' },
      },
      {
        key: 'suprai',
        label: 'AI, MarTech & Group Services',
        title: 'The Central Nervous System',
        copy:
          'SuprAI Pte Ltd builds the Group’s AI, automation and MarTech tools — proven first on our own businesses, then productised for clients. From cost centre to profit centre.',
        link: { label: 'How SuprAI works →', to: '/our-businesses#suprai' },
      },
      {
        key: 'technology',
        label: 'Technology Jumpstart & RWA',
        title: 'The Capability Accelerator',
        copy:
          'A strategic associate stake in a technology start-up jumpstarts the Group’s technology and product capability — with real-world-asset tokenisation as regulator-validated optionality for the future.',
        link: { label: 'Our technology pathway →', to: '/our-businesses#technology' },
      },
    ],
  },
  fishingApp: {
    eyebrow: 'Applied intelligence · Intelligent Fishing Application',
    h2: 'Intelligence, where the work actually happens.',
    intro:
      'The Intelligent Fishing Application is built for the waters the Group has served for over half a century. It turns satellite, oceanographic and vessel data into decisions a captain can act on before leaving harbour — sea-surface temperature, wave height, chlorophyll, wind, species forecasts and navigation, in three languages, on a phone, offline.',
    tiles: [
      {
        title: 'Trilingual by default',
        copy: 'English · Bahasa Melayu · Bahasa Indonesia — built for the crews who actually use it.',
      },
      {
        title: 'Works offline',
        copy: 'Offline maps and a data-saving mode, because coverage ends at the coastline.',
      },
      {
        title: 'Fleet-aware',
        copy: 'Vessel positions, tracks and safety layers on one chart.',
      },
    ],
    closing:
      'The Intelligent Fishing Application is a Group capability, developed by SuprAI Pte Ltd on the operating knowledge of Supratechnic and SII.',
  },
  techBand: {
    eyebrow: 'Where technology meets the Group',
    h2: 'Three surfaces, one capability stack.',
    tiles: [
      {
        key: 'fishingApp',
        eyebrow: 'Applied AI',
        title: 'Intelligent Fishing Application',
        copy: 'Marine and fisheries intelligence, running on the waters the Group already serves.',
        link: { label: 'See the ocean scene →', to: '/#intelligent-fishing' },
        poster: '/posters/ocean-data.webp',
        posterAlt:
          'A still of the ocean-data scene showing a sea-surface temperature ramp over the Malacca Strait.',
      },
      {
        key: 'suprai',
        eyebrow: 'The SuprAI stack',
        title: 'Signal in, structure out',
        copy: 'Disordered operating data resolved into tools that sharpen every business.',
        link: { label: 'How SuprAI works →', to: '/our-businesses#suprai' },
        poster: '/posters/signal-lattice.webp',
        posterAlt:
          'A still of the SignalLattice scene: a turbulent particle cloud resolving into an ordered blue lattice.',
      },
      {
        key: 'rwa',
        eyebrow: 'RWA tokenisation',
        title: 'Optionality, measured',
        copy:
          'Real-world-asset tokenisation is held as regulator-validated optionality, to be explored steadily as that market matures.',
        link: { label: 'Our technology pathway →', to: '/our-businesses#technology' },
        poster: '/posters/token-chain.webp',
        posterAlt:
          'A still of a vertical chain of translucent linked blocks descending into navy depth, each carrying a hash string.',
      },
    ],
  },
  cta: {
    h2: 'Built on discipline. Positioned for the future.',
    copy:
      'As we resume trading on the SGX Mainboard, our commitment is to transparency, sound governance, and a clear strategy for value creation.',
    button: { label: 'Investor Relations →', to: '/investor-relations' },
    micro: 'Share price and financial information update once trading resumes.',
  },
};

/* ── The flywheel ── reused on Home and The Group ────────────────────── */
export const FLYWHEEL = {
  eyebrow: 'The Flywheel',
  h2: 'How the Group compounds.',
  closing: 'The flywheel turns again, faster each time.',
  groupCopy:
    'Distribution generates revenue and data. AI and MarTech turn that data into tools that sharpen every business. A technology start-up accelerates the build. Capital and talent flow back into distribution — and the flywheel turns again, faster each time.',
  stages: [
    {
      key: 'distribution',
      title: 'Distribution',
      copy:
        'Distribution generates revenue and real-world data across three countries — marine, industrial, healthcare and scientific instrumentation.',
    },
    {
      key: 'ai',
      title: 'AI & MarTech',
      copy:
        'AI and MarTech turn that data into tools that sharpen every operating entity — proven internally, then sold externally.',
    },
    {
      key: 'frac',
      title: 'Technology Jumpstart',
      copy:
        'A technology start-up jumpstarts the Group’s technology and project capability, with RWA tokenisation as parallel optionality.',
    },
    {
      key: 'capital',
      title: 'Capital & Talent',
      copy: 'Capital and talent flow back into distribution — and the flywheel turns again, faster each time.',
    },
  ],
};

/* ── The Group ──────────────────────────────────────────── */
export const GROUP = {
  overview: {
    h2: 'About the Group',
    paragraphs: [
      `The Group is a Singapore-headquartered group listed on the SGX Mainboard, with operations across Singapore, Malaysia and Indonesia. For more than ${HERITAGE_YEARS} years, our distribution businesses have supplied premium marine, industrial, healthcare and scientific instrumentation to customers in regulated, demanding industries.`,
      'Today, the Group is being transformed. We are building artificial intelligence, automation and marketing-technology capabilities — proving them first across our own operating companies, then offering them to clients across the region and beyond. Our ambition is to evolve from a respected regional distributor into a two-engine group: an established revenue base, and a growing intelligence business built on top of it.',
    ],
    pullQuote: 'One group, applied with discipline, compounding into trust — and into value.',
  },
  pillars: {
    h2: 'Three pillars, working as one',
    intro:
      'Our strategy rests on three pillars under a single listed holding company. Each strengthens the next.',
    paragraphs: [
      `Supratechnic and SII are the Group’s revenue engine: ${HERITAGE_YEARS}+ years of distribution across marine, industrial, healthcare and scientific instrumentation in three countries. They generate recurring revenue and the real-world operating data that powers everything above them.`,
      'SuprAI Pte Ltd is the Group’s central nervous system. It builds AI, automation and MarTech products, deploys them first on our own businesses, and centralises shared services across the Group. Proven internally, the stack becomes a product we can sell.',
      'Through a strategic minority stake in a technology start-up, the Group accelerates the technology, product-development and project-management capabilities it needs today — with real-world-asset tokenisation as regulator-validated optionality for the future.',
    ],
  },
  milestones: {
    h2: 'Our journey',
    intro: 'From a regional distribution business to a listed group in transformation.',
    items: [
      {
        year: '1968',
        text: 'Supratechnic Pte Ltd incorporated in Singapore, laying the foundations of the Group’s distribution business.',
      },
      {
        year: '1975',
        text: 'SII established, bringing scientific and industrial instrumentation to the Group.',
      },
      {
        year: '31 Jan 2007',
        text: 'Listed on the Mainboard of the Singapore Exchange.',
      },
      { year: '23 Feb 2024', text: 'Trading in the Company’s shares suspended.' },
      {
        year: '11 Jun 2024',
        text: 'Joint and Several Judicial Managers appointed.',
        detail: 'Interim judicial managers had been appointed on 11 March 2024.',
      },
      {
        year: '19 Sep 2025',
        text: 'Conditional Loan Facility Agreement with Ascendro Group Limited.',
        detail:
          'Ascendro committed S$3.0 million — S$2.0 million to fund distributions to creditors under the scheme of arrangement and S$1.0 million for professional fees and restructuring expenses.',
      },
      { year: '9 Apr 2026', text: 'Creditors approve the scheme of arrangement.' },
      { year: '21 May 2026', text: 'The High Court sanctions the scheme of arrangement.' },
      { year: '18 Jul 2026', text: 'The Company is discharged from judicial management.' },
      {
        year: '30 Jul 2026',
        text:
          'Board reconstituted with a majority of independent directors, and the AI and Technology Committee established.',
      },
      {
        year: 'Aug 2026',
        text:
          'Appointed authorised Tohatsu outboard dealer for Peninsular Malaysia, and SII entered a joint venture with the Kimera Health group on 26 August 2026.',
      },
      {
        year: '26 Oct 2026',
        text: 'Audited FY2026 financial statements released with an unmodified audit opinion.',
      },
      { year: '12 Nov 2026', text: 'Combined annual general meeting for FY2022 to FY2026.' },
      {
        year: 'Pending',
        pending: true,
        text: 'Resumption of trading on the SGX Mainboard.',
        detail: 'Subject to the approval of the SGX-ST, and not assured.',
      },
    ],
  },
  structure: {
    h2: 'How we are organised',
    intro:
      'The Group is the listed holding company. Beneath it sit the operating and capital entities that make up the three pillars.',
    notesLabel: 'Notes',
    notes: [
      'SuprAI Pte. Ltd. was formerly Supratechnic SII Pte. Ltd.; SuprAI Capital Pte. Ltd. was formerly Supratechnic Capital Pte. Ltd.',
      'Scientific & Industrial Instrumentation Pte. Ltd. is held 100% by SuprAI Pte. Ltd.',
      'PT Supratechnic Instrumentasi is held 90% by Supratechnic Pte. Ltd. and 10% by Supratechnic (M) Sdn. Bhd.',
      'Subsidiaries that are dormant, in liquidation or being struck off, and proposed investments, are not shown. Details of all subsidiaries are set out in the audited financial statements for FY2026.',
    ],
  },
  leadership: {
    h2: 'Leadership',
    intro:
      'Senior management and the Joint Company Secretaries, as published in the Annual Report 2026.',
    source: 'Per the Company’s Annual Report 2026 (28 October 2026).',
    pending: 'Leadership profiles will be published following Board and Company Secretary approval.',
  },
};

/**
 * Group structure tree — the corporate structure chart supplied on 29 October
 * 2026, reproduced exactly: nine entities, the charted descriptors, percentages
 * and segments, in the chart's left-to-right order. Nothing is added and nothing
 * is inferred; the four notes beneath the chart are in GROUP.structure.notes.
 *
 * `name: null` on the root means "use brand.legalName" so the rebrand cannot
 * leave a stale entity name behind. `type` drives the legend, `segments` the
 * segment chips, and `crossHolding` the dashed 10% link between
 * Supratechnic (M) Sdn. Bhd. and PT Supratechnic Instrumentasi.
 */
export const STRUCTURE_TREE = {
  name: null,
  role: 'Singapore \u00b7 Investment holding',
  detail: 'Listed on the SGX-ST Mainboard',
  type: 'listed',
  children: [
    {
      name: 'SuprAI Pte. Ltd.',
      stake: '100%',
      role: 'Singapore \u00b7 Intermediate holding company',
      type: 'holding',
      children: [
        {
          name: 'Supratechnic Pte. Ltd.',
          stake: '100%',
          role: 'Singapore \u00b7 Distribution holding company',
          type: 'holding',
          children: [
            {
              name: 'Supratechnic (S) Pte. Ltd.',
              stake: '100%',
              role: 'Singapore \u00b7 Marine distribution; IQAir distribution',
              type: 'operating',
              segments: ['marine', 'healthcare'],
            },
            {
              name: 'Supratechnic (M) Sdn. Bhd.',
              stake: '100%',
              role: 'Malaysia \u00b7 Outboard motor distribution and services',
              type: 'operating',
              segments: ['marine'],
              children: [
                {
                  name: 'Supratechnic Properties Sdn. Bhd.',
                  stake: '100%',
                  role: 'Malaysia \u00b7 Property holding',
                  type: 'dormant',
                },
              ],
            },
            {
              name: 'PT Supratechnic Instrumentasi',
              stake: '90%',
              role: 'Indonesia \u00b7 Laboratory instruments and IQAir distribution',
              type: 'operating',
              segments: ['instrumentation', 'healthcare'],
              crossHolding: { stake: '10%', holder: 'Supratechnic (M) Sdn. Bhd.' },
            },
          ],
        },
        {
          name: 'Scientific & Industrial Instrumentation Pte. Ltd.',
          stake: '100%',
          role: 'Singapore \u00b7 Laboratory, calibration, validation and gas detection',
          type: 'operating',
          segments: ['instrumentation'],
        },
        {
          name: 'SuprAI Capital Pte. Ltd.',
          stake: '100%',
          role: 'Singapore \u00b7 Investment holding',
          type: 'holding',
        },
      ],
    },
  ],
};

/* ── Resumption roadmap ───────────────────────────────────── */
/**
 * Every milestone below is taken from the Company's own SGXNet filings, which are
 * published on this site. `slug` links the milestone to the filing it comes from;
 * where a milestone is stated inside another filing rather than announced on its
 * own, `sourceNote` says which one. Nothing here is a forecast except the final
 * entry, which is explicitly marked as pending and carries no date.
 */
export const RESUMPTION = {
  eyebrow: 'Trading resumption',
  h2: 'The road back to trading',
  intro:
    'Shares in the Company are suspended. This is the sequence of steps the Board has taken since the Company was discharged from judicial management, each one drawn from the Group’s own announcements to SGXNet.',
  progressNote:
    'Progress counts the steps the Company has announced, not elapsed time towards a resumption date. The dates on the remaining steps are the deadlines to which SGX RegCo and ACRA have extended time, not a resumption date: resumption itself depends on the proposal submitted to Singapore Exchange Regulation and on its approvals, and no resumption date has been announced.',
  footnote:
    'Milestones are drawn from the filings listed above and carry no forward-looking commitment. Resumption is subject to the requisite approvals, including those of Singapore Exchange Regulation.',
  milestones: [
    {
      date: '2026-07-02',
      title: 'Judicial management: application for approval of fees, discharge and release',
      detail: 'The judicial managers applied to court for approval of their fees, and for their discharge and release.',
      slug: '2026-07-02-judicial-management-application-for-approval-of-fees-discharge-and-release',
      state: 'done',
    },
    {
      date: '2026-07-18',
      title: 'Discharged from judicial management',
      detail:
        'The Company’s judicial management was discharged with effect from 18 July 2026 — the key milestone in the Group’s restructuring.',
      sourceNote: 'Stated in the announcements of 4 and 5 August 2026.',
      slug: '2026-08-04-press-release-appointment-of-sac-capital-as-financial-adviser',
      state: 'done',
      emphasis: true,
    },
    {
      date: '2026-07-22',
      title: 'Joint Company Secretaries appointed',
      detail: 'Mr Leow Yong Kin and Ms Kiar Lee Noi were appointed Joint Company Secretaries.',
      slug: '2026-07-22-appointment-of-joint-company-secretaries',
      state: 'done',
    },
    {
      date: '2026-07-30',
      title: 'Board reconstituted and AI and Technology Committee established',
      detail:
        'A refreshed Board was announced, together with the composition of the Board committees and the constitution of the AI and Technology Committee.',
      slug: '2026-07-30-board-composition-and-constitution-of-the-ai-and-technology-committee',
      state: 'done',
      emphasis: true,
    },
    {
      date: '2026-08-03',
      title: 'Chief Financial Officer and Joint Company Secretary appointed',
      detail: 'The finance function was brought back under a permanent Chief Financial Officer.',
      slug: '2026-08-03-appointment-of-chief-financial-officer-and-joint-company-secretary',
      state: 'done',
    },
    {
      date: '2026-08-04',
      title: 'SAC Capital appointed Financial Adviser for the resumption of trading',
      detail:
        'SAC Capital Private Limited was appointed to advise on the trading resumption proposal and to provide corporate access to investors aligned with the Group’s refreshed business model.',
      slug: '2026-08-04-press-release-appointment-of-sac-capital-as-financial-adviser',
      state: 'done',
      emphasis: true,
    },
    {
      date: '2026-08-05',
      title: 'Rajah & Tann Singapore LLP appointed Legal Adviser',
      detail:
        'Legal advisers were appointed to advise on options and strategies to resume trading of the Company’s shares.',
      slug: '2026-08-05-appointment-of-rajah-and-tann-singapore-llp-as-legal-adviser',
      state: 'done',
      emphasis: true,
    },
    {
      date: '2026-08-12',
      title: 'Letter of financial support received',
      detail: 'The Company announced its receipt of a letter of financial support.',
      slug: '2026-08-12-receipt-of-letter-of-financial-support',
      state: 'done',
    },
    {
      date: '2026-08-14',
      title: 'Proposed loan conversion and issue of conversion shares',
      detail: 'A proposed conversion of loans into new shares was announced.',
      slug: '2026-08-14-proposed-loan-conversion-and-issue-of-conversion-shares',
      state: 'done',
    },
    {
      date: '2026-08-26',
      title: 'SII joint venture and letter of intent with the Kimera Health group',
      detail:
        'SII entered an unincorporated joint venture with the three Kimera Health companies to take instrumentation, calibration, validation and IQAir clean air into regulated healthcare channels in Singapore, Malaysia and Indonesia — no joint venture company, no capital contribution and no consideration. A separate, non-binding letter of intent records an intention to negotiate SII acquiring up to 51% of one or more of those companies, subject to due diligence, valuation, definitive documents and the requisite approvals.',
      slug: '2026-08-26-entry-into-a-joint-venture-and-letter-of-intent-with-kimera-health-group',
      state: 'done',
      emphasis: true,
    },
    {
      date: '2026-08-31',
      title: 'Audited financial statements for FY2022 to FY2024 released',
      detail:
        'The Company released the audited financial statements for the financial years ended 31 March 2022, 2023 and 2024, together with statements of the discrepancies between the unaudited and the audited accounts for FY2022 and FY2023. The independent auditor issued a disclaimer of opinion on the audited financial statements for FY2022 to FY2024.',
      slug: '2026-08-31-notification-of-release-of-the-audited-financial-statements-for-fy2022-2024',
      state: 'done',
      emphasis: true,
    },
    {
      date: '2026-09-30',
      title: 'Audited financial statements for FY2025 released — the accounts are current',
      detail:
        'The Company released the audited financial statements for the financial year ended 31 March 2025, bringing the Group’s audited accounts up to date. The independent auditor disclaimed an opinion on the consolidated financial performance and cash flows for the year, being unable to obtain sufficient evidence on the opening balances, and gave an unmodified opinion on the consolidated financial position as at 31 March 2025.',
      slug: '2026-09-30-full-yearly-results',
      state: 'done',
      emphasis: true,
    },
    {
      date: '2026-10-07',
      title: 'SGX RegCo no-objection to the waivers and extensions of time',
      detail:
        'SGX RegCo informed the Company on 6 October 2026 that it has no objection to the Company’s waiver and extension of time applications, subject to conditions the Board confirms have been met, and granted an extension for the Company to submit its trading resumption proposal by 31 October 2026. ACRA responded on 5 October 2026 and has agreed to withhold enforcement action until 31 December 2026, in view of the Company’s commitment to file the outstanding annual returns by that date.',
      slug: '2026-10-07-sgx-regco-no-objection-letter-on-waivers-and-extensions-of-time-and-outcome-from',
      state: 'done',
      emphasis: true,
    },
    {
      date: '2026-10-26',
      title: 'Audited FY2026 financial statements released with an unmodified audit opinion',
      detail:
        'The audited financial statements for the financial year ended 31 March 2026 were released on 26 October 2026, and the independent auditor issued an unmodified opinion on them — the condition SGX RegCo attached to the Company’s trading resumption proposal.',
      slug: '2026-10-26-full-yearly-results',
      state: 'done',
      emphasis: true,
    },
    {
      date: '2026-10-31',
      title: 'Trading resumption proposal',
      detail:
        'The resumption proposal is being progressed with the Financial Adviser and the Legal Adviser for submission to Singapore Exchange Regulation by 31 October 2026, the date to which SGX RegCo has extended the deadline.',
      state: 'pending',
    },
    {
      date: '2026-11-12',
      title: 'Combined FY2022–FY2026 Annual Report issued and a single AGM convened',
      detail:
        'The combined Annual Report for FY2022 to FY2026 was released on SGXNet on 27 October 2026, within the extension of time to 30 November 2026. The single annual general meeting covering those five financial years is to be held on 12 November 2026 at Seletar Country Club. Separate Sustainability Reports for FY2022–FY2026 have been waived; the Company intends to comply with the sustainability reporting requirements from FY2027 onwards.',
      slug: '2026-10-27-annual-reports-and-related-documents',
      state: 'pending',
    },
    {
      date: '2026-12-31',
      title: 'Outstanding annual returns filed with ACRA',
      detail:
        'The Company remains committed to filing the outstanding annual returns by 31 December 2026, the date to which ACRA has agreed to withhold enforcement action.',
      state: 'pending',
    },
  ],
};

/* ── Our Businesses ───────────────────────────────────────── */
export const BUSINESSES = {
  sub:
    'Each of our three pillars operates with focused expertise, yet they feed one another in a powerful ecosystem. Together, they generate the data, intelligence, and capability that define our competitive edge.',
  pillar1: {
    eyebrow: 'Pillar 1',
    h2: 'The Revenue Engine',
    intro:
      `Supratechnic and SII together form the cash-generating backbone of the Group — a multi-country, multi-sector distribution platform built over more than ${HERITAGE_YEARS} years.`,
  },
  supratechnic: {
    h3: 'Supratechnic',
    tagline: 'Marine · Industrial · Healthcare distribution',
    copy:
      'Supratechnic distributes premium marine engines, industrial equipment and healthcare and air-quality solutions across Singapore, Malaysia and Indonesia. Long-standing partnerships with leading brands underpin a reputation built on technical credibility and high service standards. The business is extending into e-commerce — beginning with SupraYi — to reach customers directly.',
    facts: [
      'Incorporated 1968',
      'Singapore · Malaysia · Indonesia',
      'Ranked #249, Singapore SME 500 (2009)',
      'Afli exclusive distributor — SG · MY · ID',
    ],
    afli: {
      label: 'October 2026',
      testId: 'supratechnic-afli',
      logoKey: 'brands/afli',
      h4: 'Afli — AI fishing predictions for small-scale fishermen',
      copy:
        'Supratechnic has signed a Collaboration Agreement with Icelandic fisheries technology company GreenFish hf. and becomes the exclusive distributor of Afli in Malaysia, Singapore and Indonesia; SuprAI Capital leads the Group’s direction for Afli with GreenFish. Afli combines AI, satellite observations and oceanographic data to highlight where fish are more likely to be found, refreshed at least every six hours, and works offline at sea. Planned trip-safety features include an overdue alert to a shore contact and an emergency function, and each fisherman controls whether his position is shared. A free pilot for 50 fishermen from at least three branches on both coasts of Peninsular Malaysia runs four to six months through the monsoon, ahead of a commercial launch across the seven Malaysian branches.',
      note:
        'Afli has no satellite connection and is not a substitute for statutory safety equipment.',
      linkLabel: 'The signing, in pictures →',
      to: '/newsroom/2026-10-14-suprai-supratechnic-greenfish-afli-agreement',
      demo: {
        label: 'Watch the app working →',
        note: 'GreenFish’s walkthrough · 9 min 29 s · Malay captions',
        alt:
          'A still from the Afli walkthrough: the app showing wind, wave and temperature conditions over the waters off Peninsular Malaysia and Sumatra.',
        to: '/newsroom/2026-10-14-suprai-supratechnic-greenfish-afli-agreement#afli-demo',
      },
    },
    heritage:
      'In 1968, Supratechnic Pte Ltd was incorporated in Singapore. In 2009, Supratechnic was ranked 249 in the Singapore Small and Medium Enterprises (SME) 500 listing.',
    links: [
      { label: 'Supratechnic.com →', href: 'https://www.supratechnic.com/' },
      { label: 'SupraYi.com →', href: 'https://suprayi.com/' },
    ],
    aftersales: {
      eyebrow: 'Sales · Service · Spare parts',
      h4: 'Aftersales, not just supply',
      copy:
        'A distribution business is judged on what happens after the invoice. Supratechnic sells, services and stocks parts for the brands it represents — from its workshop and a mobile service team that goes to the boat.',
      items: [
        {
          key: 'sales',
          title: 'Sales',
          copy: 'Marine engines and equipment from our brand partners, across Singapore, Malaysia and Indonesia.',
        },
        {
          key: 'service',
          title: 'Service',
          copy: 'Trained technicians, in the workshop or at the berth, keeping engines running through their service life.',
        },
        {
          key: 'parts',
          title: 'Spare parts',
          copy: 'Genuine parts for the engines and equipment we supply, so a repair does not become a rebuild.',
        },
      ],
      caption: 'Powering marine excellence since 1968 — the workshop and mobile service team.',
    },
    outboards: {
      h4: 'Outboard motors',
      intro:
        'Supratechnic carries the HIDEA outboard range across the region, and in August 2026 Supratechnic (M) Sdn. Bhd. was appointed an authorised dealer of Tohatsu outboard motors for Peninsular Malaysia.',
      footnote:
        'Series and model designations are the manufacturers’ own. Product photography for both ranges is pending brand-owner consent, so nothing is illustrated that we cannot license.',
    },
    warranty: {
      eyebrow: 'Embracing technology',
      h4: 'Asia’s first fully digital online warranty system for marine outboard motors',
      copy:
        'One platform that seamlessly combines the distributor, the supplier and the customer. The customer scans a QR code to register a product; the branch completes a digital pre-delivery inspection; HQ verifies it; and the supplier approves the case in the same system. Claims run the same way, from the fault being reported to the repair being closed.',
      builtBy:
        'Built by SuprAI and Frac on Microsoft Power Platform, and written for the floor in English, Bahasa Melayu and Mandarin together. The platform is product- and supplier-agnostic: it will carry any marine product and any supplier the Group distributes for. The screens below are from our first deployment, with HIDEA.',
      roles: [
        { label: 'Customer', copy: 'Scans the QR code on the product to register it and to report a fault.' },
        { label: 'Branch', copy: 'Completes the digital pre-delivery inspection and raises the claim.' },
        { label: 'HQ', copy: 'Verifies the record and prepares the review pack for the supplier.' },
        {
          label: 'Supplier',
          copy: 'Approves, rejects, requests more information or holds for parts — and sees only its own queue, nothing else.',
        },
      ],
      facts: [
        'One platform, three parties',
        'Any marine product, any supplier',
        'QR-code registration',
        'Digital PDI, no paper',
        'Role-scoped access',
        'English · Bahasa Melayu · 中文',
      ],
      link: { label: 'Read the operating handbooks →', href: 'https://hideawarrantyapp.netlify.app/' },
      note:
        'Screens are from the platform’s first deployment, with HIDEA, and are taken from its own operating handbooks. Staff and customer identifiers are redacted.',
    },
  },
  suprayi: {
    h3: 'SupraYi',
    tagline: 'Clean air · IQAir · Direct-to-customer',
    copy:
      'SupraYi is the Group’s direct-to-customer clean-air business, operated by Supratechnic (S) Pte Ltd as the authorised Singapore distributor for IQAir — the Swiss air-quality brand trusted by hospitals and governments where clean air is non-negotiable. It is also the Group’s proving ground for e-commerce, MarTech and customer-data capability.',
  },
  sii: {
    h3: 'SII — Scientific & Industrial Instrumentation',
    tagline: 'Laboratory · Calibration · Gas detection',
    copy:
      'SII delivers precision instrumentation, calibration and gas-detection solutions to regulated industries. Its accredited laboratory and calibration services anchor the Group’s credibility with enterprise and institutional clients — and provide a natural on-ramp for future AI-driven analytics.',
    facts: [
      'Established 1975',
      'SIICAL in-house calibration laboratory',
      'Singapore · Malaysia · Indonesia · Hong Kong · New Zealand',
      '1,000+ instruments calibrated each year',
      'IGD authorised distributor — Singapore',
      'Kimera Health JV — SG · MY · ID',
    ],
    igd: {
      label: 'September 2026',
      testId: 'sii-igd-distributor',
      copy:
        'International Gas Detectors — the UK fixed gas detection manufacturer within Sweden-headquartered Safe Monitoring Group — has appointed SII its authorised distributor and channel partner in Singapore. The Distributor and Channel Partner Agreement was signed on 29 September 2026 for an initial term of thirty-six months and covers promotion, sale, system design support, installation, commissioning, servicing and calibration of IGD’s gas detection products across Singapore. It pairs IGD’s UK-engineered detection technology with SIICAL, our accredited calibration laboratory, so a customer buys the system, the commissioning and the calibration from one place.',
      linkLabel: 'The joint press release →',
      to: '/newsroom/2026-09-30-sii-appointed-igd-authorised-distributor-in-singapore',
    },
    jv: {
      label: 'August 2026',
      testId: 'sii-kimera-jv',
      copy:
        'SII has entered a joint venture and commercial collaboration agreement with the three Kimera Health companies, signed at CPHI Korea 2026 in Seoul, covering Singapore, Malaysia and Indonesia. It pairs SII’s instrumentation, calibration, validation and environmental monitoring — and the Group’s IQAir clean air systems — with Kimera Health’s medical device licences and its access to hospitals, surgical centres and specialist practices. The joint venture is unincorporated: no new company, no capital contribution and no consideration. A separate letter of intent, which is not binding, records an intention to negotiate SII acquiring up to 51% of one or more of those companies.',
      linkLabel: 'The SGXNet announcement →',
      slug: '2026-08-26-entry-into-a-joint-venture-and-letter-of-intent-with-kimera-health-group',
    },
    supportingLine:
      'The single most important question for a validation manager is who calibrates the calibrator. In our case, we do.',
    link: { label: 'SII.asia →', href: 'https://sii.asia/' },
  },
  siiPartners: [
    { name: 'Ellab', line: 'Wired and wireless thermal validation.', detail: 'E-Val Pro, TrackSense Pro Mini/Micro, sensor arrays.' },
    {
      name: 'IGD (International Gas Detectors)',
      line: 'UK-engineered industrial gas detection.',
      detail: 'Authorised distributor for Singapore since September 2026. MK8 pellistor, VOC range, addressable control panels.',
    },
    {
      name: 'LogiCO2',
      line: 'CO₂ safety for beverage, hospitality and healthcare.',
      detail: 'Mk9, Mk10, Scout Glow, SMB Safety Valve.',
    },
    { name: 'Dynament', line: 'Infrared gas-sensor technology.', detail: 'High-stability IR sensor modules.' },
  ],
  pillar2: {
    eyebrow: 'Pillar 2',
    h2: 'The Central Nervous System',
    intro:
      'SuprAI Pte Ltd builds the Group’s AI, automation and MarTech products — and runs the shared services that hold the Group together.',
    blocks: [
      {
        title: 'Prove',
        copy:
          'Our own businesses are SuprAI’s first customers. Every tool is deployed on real operations, and the intelligence from those businesses feeds back into stronger products.',
      },
      {
        title: 'Productise',
        copy:
          'When a tool demonstrably drives traffic, footfall and revenue for our own businesses, that proven traction — real customers, real numbers — turns it into a credible, sellable product.',
      },
      {
        title: 'Expand',
        copy:
          'We sell from our core strengths first — Singapore and Malaysia — then Indonesia and the wider region, then beyond. From a Group cost centre today to a profit centre tomorrow.',
      },
      {
        title: 'Group services',
        copy:
          'SuprAI Pte Ltd also centralises finance, HR, IT, legal and commercial governance across the Group — reducing duplicated overhead and setting one operating standard across three countries.',
      },
    ],
    builtAndRunning: 'Built and running',
  },
  pillar3: {
    eyebrow: 'Pillar 3',
    h2: 'The Capability Accelerator',
    intro:
      'Through a proposed strategic minority stake in a technology start-up, the Group gains a head start on the technology and discipline its AI ambitions require.',
    blocks: [
      {
        title: 'Jumpstart',
        copy:
          'The start-up brings project-management, product-development and core-technology capabilities the Group does not yet have in-house — allowing us to start building immediately rather than from zero.',
      },
      {
        title: 'Optionality',
        copy:
          'Its validation under the Malaysian Securities Commission’s recognised-market-operator framework gives the Group a regulator-endorsed entry point into real-world-asset tokenisation — to be explored steadily as that market matures.',
      },
      {
        title: 'Sequencing',
        copy:
          'AI, automation and MarTech come first — the immediate need of every SME. Real-world-asset tokenisation runs in parallel and scales later.',
      },
    ],
    disclaimer:
      'The investment in the technology start-up is proposed and subject to the requisite approvals. Nothing on this page constitutes an offer or solicitation.',
  },
};

/* ── Governance ─────────────────────────────────────────── */
export const GOVERNANCE = {
  sub:
    'Sound governance is the foundation of the trust we are rebuilding with our stakeholders. The Group is committed to the principles of the Singapore Code of Corporate Governance and to transparent, accountable leadership.',
  board: {
    h2: 'Board of Directors',
    intro: 'Our Board brings together experience across capital markets, industry, finance and governance.',
    pending: 'Director profiles will be published following Board and Company Secretary approval.',
  },
  exco: {
    h2: 'Executive Committee',
    intro:
      'The Group Executive Committee leads day-to-day management across our three countries and three pillars.',
    pending: 'Director profiles will be published following Board and Company Secretary approval.',
  },
  committees: {
    h2: 'Board committees',
    intro: 'The Board is supported by four committees, each with its own composition and remit.',
    items: [
      {
        name: 'Audit and Risk Management Committee',
        chair: 'Mr Toh Hai Joo',
        members: ['Mr Chan Chung Khang', 'Mr Jason Fong Jian Sheng'],
        note: 'Reconstituted 30 July 2026.',
      },
      {
        name: 'Nominating Committee',
        chair: 'Mr Jason Fong Jian Sheng',
        members: ['Mr Chan Chung Khang', 'Mr Toh Hai Joo'],
        note: 'Reconstituted 30 July 2026.',
      },
      {
        name: 'Remuneration Committee',
        chair: 'Mr Chan Chung Khang',
        members: ['Mr Jason Fong Jian Sheng', 'Mr Toh Hai Joo'],
        note: 'Reconstituted 30 July 2026.',
      },
      {
        name: 'AI and Technology Committee',
        chair: 'Ms Maio Dangi',
        members: ['Mr Melvin Tan Wei Yang', 'Mr Chan Chung Khang', 'Mr Jason Fong Jian Sheng'],
        note: 'Established 30 July 2026. Terms of Reference adopted 16 October 2026.',
      },
    ],
    source: 'Per the Company\u2019s Annual Report 2026 (28 October 2026).',
  },
  policies: {
    h2: 'Corporate governance & policies',
    intro: 'Our governance framework and key policies are available below.',
    items: [
      {
        id: 'corporate-governance-report',
        title: 'Corporate Governance Report',
        caption: 'Annual Report 2026 · pages 15–79',
        linkLabel: 'Read it in the Annual Report →',
        to: '/investor-relations/annual-reports',
        verifiedLabel: 'Verified',
        verifiedTo: '/investor-relations/verified-documents#annual-report-2026',
      },
      {
        id: 'code-of-conduct',
        title: 'Code of Business Conduct and Ethics (Board)',
        caption: 'Adopted 16 October 2026 · document to follow',
      },
      {
        id: 'whistleblowing',
        title: 'Whistleblowing Policy',
        caption: 'Adopted 16 October 2026 · document to follow',
        note:
          'Employees of the Group and external parties may raise concerns to the Chairperson of the Audit and Risk Management Committee. The identity of the whistleblower is kept confidential at all times.',
        email: 'whistleblowing@uspgroup.com.sg',
      },
      {
        id: 'dividend-policy',
        title: 'Dividend Policy',
        statement:
          'The Company does not currently have a formal dividend policy. It intends to establish and disclose one in its Annual Report for FY2027. The Board has not recommended a dividend for FY2026.',
      },
      {
        id: 'investor-relations-policy',
        title: 'Investor Relations Policy',
        statement:
          'The Company intends to adopt a formal investor relations policy in FY2027, ahead of Rule 710B of the Listing Manual taking effect on 1 January 2027.',
      },
    ],
    source: 'Per the Company’s Annual Report 2026 (28 October 2026).',
  },
};

/* ── Sustainability ──────────────────────────────────────── */
export const SUSTAINABILITY = {
  intro:
    'As an SGX-listed group, we recognise our responsibility to operate sustainably and to report transparently on our environmental, social and governance performance.',
  reporting: {
    h2: 'Sustainability reporting',
    paragraphs: [
      'SGX RegCo has granted the Company a waiver from the sustainability reporting requirements for FY2022 to FY2026. The Company announced the details of the waiver on 7 October 2026.',
      'During this period the Company was under prolonged judicial management, trading in its shares was suspended from 23 February 2024, and the Group’s operations were significantly curtailed.',
      'Following completion of its regularisation process, the Company intends to comply with the applicable sustainability reporting requirements under the SGX-ST Listing Manual from FY2027 onwards.',
    ],
  },
  foundations: {
    h2: 'Governance foundations',
    items: [
      'The Board was reconstituted in July 2026 with a majority of independent directors.',
      'The Audit and Risk Management, Nominating and Remuneration Committees are each chaired by an independent director.',
      'An AI and Technology Committee oversees the Group’s use of technology and data, and the related cybersecurity, data-protection and governance risks.',
      'A Code of Business Conduct and Ethics for members of the Board, and an updated Whistleblowing Policy, were adopted on 16 October 2026.',
      'The independent auditor issued an unmodified opinion on the FY2026 financial statements.',
    ],
  },
  people: {
    h2: 'Our people',
    text:
      'A new Employee Handbook and a merit-and-demerit framework were introduced across the Group’s operations in Malaysia, Singapore and Indonesia.',
  },
  archive: {
    h2: 'Reporting archive',
    text:
      'The Company’s first sustainability report under the SGX-ST Listing Manual is expected for FY2027.',
  },
  source: 'Per the Company’s Annual Report 2026 (28 October 2026).',
};

/* ── Newsroom ──────────────────────────────────────────── */
export const NEWSROOM = {
  eyebrow: 'Newsroom',
  h1: 'Newsroom',
  sub: 'News, media coverage and updates from across the Group.',
  press: {
    h2: 'Press releases',
    intro:
      'Our latest news and corporate updates, released in line with our regulatory disclosure obligations.',
    categoryLabel: 'Category',
    categories: [
      { value: 'all', label: 'All' },
      { value: 'corporate', label: 'Corporate' },
      { value: 'business', label: 'Business' },
      { value: 'technology', label: 'Technology' },
    ],
    fromLabel: 'From',
    toLabel: 'To',
    searchLabel: 'Search',
    searchPlaceholder: 'Search press releases',
    readLabel: 'Read →',
    readAria: 'Read the release',
    emptyLabel: 'No press releases yet',
    emptyCopy:
      'Press releases will appear here once they are issued. Releases are published in line with the Group’s regulatory disclosure obligations.',
    englishNote:
      'Press release titles and summaries stay in English: they are the wording released to SGXNet.',
  },
  coverage: {
    h2: 'Media coverage',
    intro: 'Selected coverage of the Group in the media.',
    emptyLabel: 'No coverage listed yet',
    emptyCopy: 'Selected media coverage will be listed here as it is published.',
    englishNote: 'Headlines are shown in the language the outlet published them in.',
  },
  filings: {
    h2: 'Latest SGXNet filings',
    intro:
      'The three most recent announcements the Company has released to SGXNet. This is the official record: titles and documents are reproduced exactly as filed.',
    viewAll: { label: 'View all announcements →', to: '/investor-relations/announcements' },
    empty: 'Announcements will appear here once the Group resumes filing on SGXNet.',
  },
  gallery: {
    h2: 'Media gallery',
    intro:
      'Photographs of the Group at work — our people, our laboratory and our installations. Each image records where it is published and what it shows. Available for editorial use on request.',
    featureEyebrow: 'Since 1975',
    sourceLabel: 'Source',
    requestLabel: 'Request the original file →',
  },
  leadership: {
    h2: 'The CEO in the market',
    intro:
      'Melvin Tan Wei Yang, Chief Executive Officer, publishes on the Group’s commercial progress and on the state of Singapore’s capital markets. Each post is linked in full.',
    linkLabel: 'Read the post on LinkedIn →',
    noImageLabel: 'Text post',
    quoteNote: 'Quotations are reproduced in the language they were published in.',
  },
  enquiries: {
    h2: 'Media enquiries',
    intro: 'For media enquiries, please contact our communications team.',
    contactLabel: 'Communications contact',
    linkLabel: 'Contact the communications team →',
  },
  assets: {
    h2: 'Image & brand assets',
    intro: 'Approved logos and imagery for media use are available on request.',
    link: { label: 'Request assets →', to: '/contact?type=Media' },
  },
};

/* ── Contact ───────────────────────────────────────────── */
export const CONTACT = {
  h1: 'Get in touch',
  intro:
    'Whether you are a customer, partner, investor or member of the media, we would be glad to hear from you.',
  privacy:
    'By submitting, you agree to our Privacy Policy. We use your details only to respond to your enquiry.',
  success: 'Thank you — your message has been received. We’ll be in touch shortly.',
  error: 'Something went wrong. Please check the highlighted fields and try again.',
  officesHeading: 'Our offices',
  registered: {
    label: 'Registered office',
    address: '141 Cecil Street, #07-06 Tung Ann Association Building, Singapore 069541',
    phone: '+65 6862 2866',
    phoneHref: 'tel:+6568622866',
    note: 'Per the Company\u2019s Annual Report 2026 (Corporate Information).',
  },
  hours: 'Monday–Friday, 8.30am – 5.00pm (Singapore time).',
  offices: [
    {
      id: 'group-hq',
      name: 'Group HQ — Singapore',
      address: '51 Bukit Batok Crescent, #03-26/27 Unity Centre, Singapore 658077',
      email: 'general@uspgroup.com.sg',
    },
    {
      id: 'supratechnic',
      name: 'Supratechnic',
      note:
        'Marine outboard sales, service and parts — branches across Singapore and Peninsular Malaysia, with a dealer network.',
      branches: [
        {
          id: 'sg',
          label: 'Singapore branch',
          address: '51 Bukit Batok Crescent #03-26/27 Unity Centre, Singapore 658077',
          phone: '+65 6862 2100',
          phoneHref: 'tel:+6568622100',
        },
        {
          id: 'my',
          label: 'Malaysia regional office',
          address:
            'No. 12A, Jalan Astaka U8/88A, Bukit Jelutong Business & Technology Centre, 40150 Shah Alam, Selangor Darul Ehsan',
          phone: '+603 7846 6262',
          phoneHref: 'tel:+60378466262',
          email: 'my@supratechnic.com',
        },
      ],
      website: 'https://supratechnic.com/contact/',
    },
    {
      id: 'indonesia',
      name: 'Supratechnic Indonesia',
      address: 'Jl. Sunter Kemayoran No. 55, Jakarta 14350, Indonesia',
      email: 'julian@supratechnic.com',
    },
    {
      id: 'sii-asia',
      name: 'SII Asia',
      address: '51 Bukit Batok Crescent #03-26 Unity Centre, Singapore 658077',
      website: 'https://sii.asia/contact/',
    },
    {
      id: 'suprayi',
      name: 'SupraYi Singapore',
      address: '51 Bukit Batok Crescent #03-26 Unity Centre, Singapore 658077',
      website: 'https://suprayi.com/contact/',
    },
  ],
};

/* ── Legal ────────────────────────────────────────────── */
export const LEGAL_PENDING =
  'This document is being finalised with the Group’s legal counsel and will be published prior to launch.';

export const IMPORTANT_NOTICE_STANDING_TEXT =
  'This website may contain forward-looking statements regarding the Group’s strategy, plans and prospects. Such statements are not guarantees of future performance and involve risks and uncertainties. Actual results may differ materially. Information on this website is provided for general information only and does not constitute an offer or solicitation to buy or sell any securities. Investors should refer to the Group’s official announcements released via SGXNet for authoritative information.';

export const LEGAL_PAGES = [
  { slug: 'privacy-policy', title: 'Privacy Policy' },
  { slug: 'terms-of-use', title: 'Terms of Use' },
  { slug: 'disclaimer', title: 'Disclaimer' },
  { slug: 'cookie-policy', title: 'Cookie Policy' },
  { slug: 'important-notice', title: 'Important Notice' },
];

export const COOKIE_COPY =
  'We use cookies to operate this site and improve your experience. You can accept all cookies or manage your preferences.';

/* ── Investor Relations ────────────────────────────────────── */
/* ── Verified documents ─────────────────────────────────────────────────────
   Facts (hash, size, seal state, block height) come from /api/verified. Only
   display copy lives here, so sealing a sixth document stays a data change.    */
export const VERIFIED = {
  eyebrow: 'Investor Relations',
  h1: 'Verified documents',
  intro:
    'Our key disclosure documents, each sealed on a public blockchain so anyone can confirm that the copy they hold is exactly the one we published.',
  notice:
    'SGXNet is the authoritative record of the Company\u2019s announcements. This page is an additional check: it proves the files below are the ones published on this website on the date shown, and that they have not been altered since.',
  steps: [
    {
      title: 'Published on SGXNet',
      text: 'Each document was first released to the market via SGXNet.',
    },
    {
      title: 'Sealed on the blockchain',
      text:
        'We recorded each file\u2019s digital fingerprint on a public blockchain \u2014 a record with a date that nobody, including the Company, can change.',
    },
    {
      title: 'Check any copy yourself',
      text:
        'Drop a PDF below; your device computes its fingerprint and tells you whether it matches.',
    },
  ],
  documents: {
    h2: 'Sealed documents',
    releasedLabel: 'Released on SGXNet',
    viewLabel: 'View document',
    technicalLabel: 'Technical details',
    fingerprintLabel: 'SHA-256 fingerprint',
    sizeLabel: 'File size',
    proofLabel: 'Download blockchain proof (.ots)',
    verifyLabel: 'Verify on OpenTimestamps',
    independent:
      'To verify independently, download the document and its proof file, open opentimestamps.org and drop both files onto the page.',
    bases:
      'The Annual Report states that the bases for the disclaimers of opinion related largely to records, confirmations and events from before and during judicial management (Annual Report 2026, page 3).',
    pending: 'Sealed on {sealed}. Blockchain confirmation in progress (usually a few hours).',
    confirmed: 'Sealed on {date}. Confirmed on the public blockchain on {blockDate} (block {height}).',
    confirmedNoTime: 'Sealed on {date}. Confirmed on the public blockchain (block {height}).',
    empty: 'Sealed documents will appear here once the first proof has been recorded.',
    /* Localised titles and opinions, keyed by slug. A slug with no entry falls
       back to the English text stored with the seal. */
    bySlug: {},
  },
  checker: {
    h2: 'Check a copy someone sent you',
    label: 'Choose a PDF file',
    hint:
      'Nothing is uploaded. Your browser reads the file and computes its fingerprint on your own device.',
    working: 'Computing fingerprint\u2026',
    yourFingerprint: 'Fingerprint of your file',
    match: 'This file is identical to {title} as published on {date}.',
    noMatch:
      'This file does not match any document on this page. It may have been altered, or it may be a different version. Check SGXNet for the authoritative copy.',
    error: 'That file could not be read. Please choose a PDF file.',
  },
  faq: {
    h2: 'Questions',
    items: [
      {
        q: 'What is a blockchain?',
        a:
          'A public record kept by thousands of computers around the world. Once something is written into it, it cannot be changed or backdated.',
      },
      {
        q: 'What does the seal prove?',
        a:
          'That these exact files were published on this website by the date shown, and that they have not been changed since. It does not prove anything about the contents beyond that; the audit opinions are stated on each card.',
      },
      {
        q: 'Does this replace SGXNet?',
        a: 'No. SGXNet remains the authoritative source for all Company announcements.',
      },
      {
        q: 'Why seal documents that are already on SGXNet?',
        a:
          'Documents are often forwarded as attachments. The check lets anyone confirm a forwarded copy is unaltered without trusting the sender.',
      },
    ],
  },
};

export const IR = {
  intro:
    'Welcome to the Group’s Investor Relations. Here you will find our regulatory announcements, financial results, share information and governance disclosures. We are committed to timely, accurate and even-handed communication with all our stakeholders.',
  tiles: [
    { title: 'Announcements', to: '/investor-relations/announcements', note: 'The authoritative record of our disclosures.' },
    { title: 'Financial results', to: '/investor-relations/financial-results', note: 'Results announcements, presentations and webcasts.' },
    { title: 'Annual reports', to: '/investor-relations/annual-reports', note: 'Annual reporting archive.' },
    { title: 'Share information', to: '/investor-relations/share-information', note: 'Listing, ticker, ISIN and trading status.' },
    { title: 'AGM & EGM', to: '/investor-relations/general-meetings', note: 'Notices, proxy forms and results of meetings.' },
    { title: 'Corporate actions', to: '/investor-relations/corporate-actions', note: 'Restructuring, capital actions and resumption of trading.' },
    { title: 'Verified documents', to: '/investor-relations/verified-documents', note: 'Blockchain-sealed disclosure documents, and a way to check any copy.' },
    { title: 'Email alerts', to: '/investor-relations/email-alerts', note: 'Announcements and results, by email.' },
    { title: 'IR contact', to: '/investor-relations/contact', note: 'Investor and analyst enquiries.' },
  ],
  announcements: {
    h2: 'Announcements',
    intro:
      'All regulatory announcements are released via SGXNet and published here. This is the authoritative record of our disclosures to the market.',
    filters: [
      { key: 'all', label: 'All' },
      { key: 'financial_results', label: 'Financial results' },
      { key: 'corporate_action', label: 'Corporate action' },
      { key: 'agm_egm', label: 'AGM/EGM' },
      { key: 'general', label: 'General' },
    ],
    empty: 'Announcements will appear here once the Group resumes filing on SGXNet.',
  },
  financial: {
    h2: 'Financial results',
    intro:
      'Results announcements, presentations and webcasts are grouped by financial year and reporting period. This page is wired directly to SGXNet: a half-year or full-year results filing appears here automatically, with its document attached, within the hour it is released.',
    empty:
      'No results announcement appears in the Company’s SGXNet filings for the last twelve months — the Company was under judicial management until its discharge with effect from 18 July 2026. The next results filing will appear here automatically, grouped by financial year and period.',
    note:
      'Each filing is placed in the financial year that SGX records on the filing itself, so nothing is inferred. The Group’s financial year ends on 31 March. The audited financial statements for FY2022 to FY2024 were released on 31 August 2026 and the independent auditor issued a disclaimer of opinion on them. For FY2025, released on 30 September 2026, the auditor disclaimed an opinion on the Group’s consolidated financial performance and cash flows and gave an unmodified opinion on the consolidated financial position. Those reports, and the statements of discrepancies between the unaudited and the audited accounts, should be read together with the accounts.',
  },
  annualReports: {
    h2: 'Annual reports',
    intro: 'The Group’s annual reporting archive.',
    empty: 'Annual reports will be published here once they are available.',
    verifiedLabel: 'Verified',
    verifiedTo: '/investor-relations/verified-documents#annual-report-2026',
  },
  shareInfo: {
    h2: 'Share information',
    intro: 'The Group is listed on the Mainboard of the Singapore Exchange.',
    footnote: 'Source: SGX. Confirm all values with the Company Secretary prior to publication.',
    priceCaption: 'Delayed price — enabled when trading resumes.',
  },
  meetings: {
    h2: 'General meetings',
    intro:
      'Notices, proxy forms, presentations, results of meetings and minutes are published here for each general meeting.',
    groups: ['Notice', 'Proxy form', 'Presentation', 'Results of meeting', 'Minutes'],
    upcoming: {
      label: 'Upcoming meeting',
      title: 'Annual General Meeting for FY2022 to FY2026',
      when: 'Thursday, 12 November 2026 at 10.00 a.m.',
      venue:
        'Seletar Country Club, Seletar Ballroom, 101 Seletar Club Road, Singapore 798273',
      note:
        'A single annual general meeting covering the five financial years ended 31 March 2022, 2023, 2024, 2025 and 2026. The notice of meeting is set out in the Annual Report 2026.',
      linkLabel: 'Read the notice in the Annual Report 2026 →',
    },
    empty: 'General meeting materials will be published here ahead of the next meeting.',
  },
  corporateActions: {
    h2: 'Corporate actions & restructuring',
    intro:
      'A clear, factual record of the Group’s recent corporate history, including our restructuring, capital actions and the resumption of trading. We believe transparency on this record is fundamental to rebuilding confidence.',
    body:
      'Following a period under Judicial Management, the Group completed a Creditors’ Scheme of Arrangement approved at the Scheme Meeting, and was discharged from Judicial Management with effect from 18 July 2026. Trading in the Company’s shares remains suspended; the resumption of trading on the SGX Mainboard is subject to the Company’s resumption proposal and the approval of the SGX-ST. Subject to shareholder and regulatory approvals, the Group also intends to change its name and to broaden its business focus to include artificial intelligence and marketing technology.',
    recordHeading: 'Filings on the record',
    feedNote:
      'Every entry below is an announcement the Company released to SGXNet and which the Group classifies as a corporate action — the scheme of arrangement, judicial management, capital and share matters, and the disposal and renaming of subsidiaries. Nothing here is written by hand: the list is drawn from the filings themselves and updates automatically as new ones are released. Times shown are the SGXNet broadcast time in Singapore time.',
    empty:
      'No corporate action filing appears in the Company’s SGXNet history yet. Filings will appear here automatically as they are released.',
    disclaimer:
      'This page is subject to review by the Group and its legal counsel prior to publication. Investors should refer to the Group’s official announcements released via SGXNet for authoritative information.',
  },
  emailAlerts: {
    h2: 'Stay informed',
    intro:
      'Register your interest and you will be notified by email of each announcement the Group releases to SGXNet, within the hour it is published — no digests, no marketing.',
    contents: [
      'The headline, and the date and time of broadcast in Singapore time',
      'A short summary where the announcement carries one',
      'A link to the announcement on this site, and the document itself',
      'The Company’s current trading status and last traded share price',
      'A one-click unsubscribe link in every email',
    ],
    consent:
      'I agree to receive investor communications from the Group and have read the Privacy Policy.',
    confirmation: 'Please check your inbox to confirm your subscription.',
    doubleOptIn:
      'Alerts are double opt-in: you will be asked to confirm the address before anything is sent to it, and you can unsubscribe from any email. Email delivery is not switched on yet — registrations are being collected now and the first alert will follow once it is live.',
  },
  contact: {
    h2: 'Contact Investor Relations',
    intro: 'For investor and analyst enquiries, please contact our Investor Relations team.',
    privacy:
      'By submitting, you agree to our Privacy Policy. We use your details only to respond to your enquiry.',
  },
  downloadCentre: 'Download Centre',
};

export const NOT_FOUND = {
  h1: 'We couldn’t find that page',
  body: 'The page you’re looking for may have moved. Try our homepage or Investor Relations.',
};

/* ── UI strings ─────────────────────────────────────────────
   Interface copy that used to sit inline in the corporate pages. It lives here
   so it can be translated alongside the page copy. English is the base. */
export const UI = {
  common: {
    home: 'Home',
    ir: 'Investor Relations →',
    search: 'Search',
    searchAria: 'Search the site',
    language: 'Language',
    languageNote:
      'The corporate pages are available in three languages. Investor Relations filings stay in English, as released to SGXNet.',
    draftLabel: 'Draft translation',
    draftNotice:
      'This page is a draft translation, pending sign-off by the Group. Where the two differ, the English version prevails.',
    englishOnlyLabel: 'English only',
    englishOnlyNotice:
      'Investor Relations and legal pages are published in English only. Announcements are the official record as released to SGXNet.',
    readInEnglish: 'Read in English',
    autoLabel: 'Language matched',
    autoNotice:
      'We matched this page to your browser language. It is a draft translation, pending sign-off by the Group — where the two differ, the English version prevails.',
    skipToContent: 'Skip to content',
    scrollToTop: 'Back to top',
    viewPhoto: 'View photograph',
    downloadPhoto: 'Download photograph',
    previousPhoto: 'Previous photograph',
    nextPhoto: 'Next photograph',
    close: 'Close',
    chapters: 'Chapters',
    playFrom: 'Play from',
    videoSilent: 'Silent recording · no narration',
    videoUnsupported: 'Your browser cannot play this video.',
    captionsMalay: 'Malay captions',
    downloadVideo: 'Download the video (MP4)',
    menu: 'Menu',
    menuNote: 'Site navigation, search and language options.',
    cookieSettings: 'Cookie settings',
    awaitingApproval: 'Awaiting approval',
    poweredBy: 'Powered by',
    personnelProfile: 'Personnel Profile',
    printDocument: 'Print or save as PDF',
    printHint: 'The printed copy carries the version, effective date and source URL.',
    skipIntro: 'Skip',
    listedOn: 'Listed on the',
    rightsReserved: 'All rights reserved.',
  },
  cookies: {
    aria: 'Cookie preferences',
    copy: COOKIE_COPY,
    policy: 'Cookie Policy',
    necessaryLabel: 'Strictly necessary',
    necessaryNote: 'required to operate this site. Always on.',
    necessaryAria: 'Strictly necessary cookies',
    analyticsLabel: 'Analytics',
    analyticsNote: 'helps us understand how the site is used.',
    marketingLabel: 'Marketing',
    marketingNote: 'measures the performance of our campaigns.',
    manage: 'Manage preferences',
    save: 'Save preferences',
    reject: 'Reject non-essential',
    accept: 'Accept all',
  },
  trading: {
    aria: 'Trading status',
    status: 'Trading status',
    resumption: 'Trading resumption',
    link: 'Share information →',
    dismiss: 'Dismiss trading status notice',
  },
  home: {
    scroll: 'Scroll',
    glanceAria: 'The Group at a glance',
  },
  group: {
    eyebrow: 'The Group',
    h1: 'The Group',
  },
  structure: {
    heading: 'Entities and ownership',
    listedHolding: 'Listed holding company',
    tapHint: 'Select an entity for its profile',
    openProfile: 'Open the profile for',
    profileEyebrow: 'Entity profile',
    activity: 'Activity',
    ownership: 'Ownership',
    heldBy: 'Held by',
    directStake: 'Direct interest',
    position: 'Position in the Group',
    subsidiaries: 'Direct subsidiaries',
    noSubsidiaries: 'No subsidiaries in the Group structure.',
    noteLabel: 'Note',
    close: 'Close',
    segmentsLabel: 'Segments:',
    crossHolding: '{stake} held by {holder}',
    chartEyebrow: 'Group structure',
    chartTitle: 'and its subsidiaries',
    scrollHint: 'Scroll the chart sideways to see every entity',
    zoomHint: 'Pinch, double-tap or use the controls to zoom into the chart',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zoomFit: 'Fit',
    type: {
      listed: 'Listed company',
      holding: 'Holding company',
      operating: 'Operating company',
      dormant: 'Dormant / property holding',
    },
    segments: {
      marine: 'Marine',
      instrumentation: 'Instrumentation',
      healthcare: 'Healthcare',
    },
  },
  fishingApp: {
    layersAria: 'Data layers',
    illustrative: 'Illustrative visualisation · no live data feed',
    play: 'Play the seven-day forecast',
    pause: 'Pause the seven-day playback',
    timeline: 'Seven-day timeline',
    days: ['Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue'],
    species: ['Indian mackerel', 'Squid', 'Tuna'],
    layers: [
      { label: 'Temperature', caption: '28.9 – 32.5 °C', detail: 'Sea-surface temperature ramp.' },
      { label: 'Waves', caption: '0.0 – 4.0 m', detail: 'Amplified swell displacement.' },
      { label: 'Chlorophyll', caption: 'Coastal bloom plumes', detail: 'Where the fish feed.' },
      { label: 'Wind', caption: '5 kn · gusts 6 kn', detail: 'Animated flow-field streaklines.' },
      { label: 'Fish', caption: 'Species probability', detail: 'Indian mackerel · Squid · Tuna' },
      { label: 'Navigation', caption: 'Depth contours · tracks · AIS', detail: 'Nautical chart mode.' },
    ],
  },
  businesses: {
    eyebrow: 'Our Businesses',
    h1: 'Our Businesses',
    brandPartners: 'Brand partners',
    typeOnlyNote:
      'Brand marks are the property of their respective owners and are shown as supplied.',
    boats: 'Kayaks',
    boatsRailAria: 'Kayaks distributed by Supratechnic',
    step: 'Step',
    suprayiClients: 'Selected SupraYi clients',
    suprayiClientsNote:
      'Published on suprayi.com as SupraYi clients. Consent to be confirmed before launch; not presented as Group-wide references.',
    iqairRailAria: 'IQAir products offered by SupraYi',
    siiPartnersNote:
      'No brand-logo files exist for these four partners, so they are set as a typographic wall rather than fabricated.',
    instrumentation: 'Instrumentation & safety products',
    instrumentationRailAria: 'Instrumentation and gas-detection products supplied by SII',
    certifications: 'Certifications',
    siiLeadership: 'SII leadership',
    fishingAppEyebrow: 'Applied intelligence · Intelligent Fishing Application',
    fishingAppTitle: 'Marine and fisheries intelligence',
    fishingAppCopy:
      'Satellite, oceanographic and vessel data turned into decisions a captain can act on before leaving harbour — trilingual, offline-capable and fleet-aware.',
    comingSoon: 'Coming soon',
    registryCaption: 'Illustrative representation of an entitlement registry.',
  },
  governance: {
    eyebrow: 'Governance',
    h1: 'Governance',
    managementTeam: 'Management Team',
    composition: 'Composition',
    chair: 'Chair',
    member: 'Member',
  },
  sustainability: {
    eyebrow: 'Sustainability',
    h1: 'Sustainability',
  },
  contact: {
    eyebrow: 'Contact',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    type: 'Enquiry type',
    subject: 'Subject',
    message: 'Message',
    select: 'Please select…',
    types: {
      General: 'General',
      Investor: 'Investor',
      Media: 'Media',
      Partnership: 'Partnership',
    },
    send: 'Send message',
    privacyBefore: 'By submitting, you agree to our ',
    privacyLink: 'Privacy Policy',
    privacyAfter: '. We use your details only to respond to your enquiry.',
    telephone: 'Telephone',
    emailLabel: 'Email',
    websiteLabel: 'Website',
    hoursLabel: 'Business hours:',
    irHeading: 'Investor Relations',
    irNote: 'For shareholder, analyst and media enquiries about the Group and its disclosures.',
    irLink: 'Investor Relations contacts →',
    errors: {
      name: 'Please enter your name.',
      email: 'Please enter a valid email address.',
      type: 'Please select an enquiry type.',
      message: 'Please enter a message.',
    },
    toastCheck: 'Please check the highlighted fields',
    toastReceived: 'Message received',
    toastError: 'Something went wrong',
  },
};
